import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isSuperAdmin } from "@/lib/roles";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Upload, Download, CheckCircle2, AlertCircle } from "lucide-react";
import { CopyFromPreviewCard } from "@/components/mobile-app/CopyFromPreviewCard";
import { ReleaseSlotCard } from "@/components/mobile-app/ReleaseSlotCard";

// Painel SA: distribuição do APK do app nativo Android + QR code pra entregadores.
// v8 (fev/2026).
//
// Fluxo:
//   1. SA/dev roda `eas build --platform android --profile preview` localmente
//   2. Baixa o .apk do EAS
//   3. Aqui: seleciona o arquivo + botão "Publicar APK" → POST /api/mobile-app/upload
//   4. Card mostra QR code apontando pra /api/mobile-app/download
//   5. Entregador aponta câmera no QR → baixa o APK direto no celular
export default function MobileAppAdmin() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [version, setVersion] = useState("1.0.0");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mobile-app/info");
      setInfo(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao consultar APK");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Selecione um arquivo .apk"); return; }
    if (!f.name.endsWith(".apk")) { toast.error("Só aceita .apk"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("version", version || "1.0.0");
    setUploading(true);
    try {
      const r = await api.post("/mobile-app/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`APK ${r.data.version} publicado (${(r.data.size_bytes / 1024 / 1024).toFixed(1)} MB)`);
      fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const downloadUrl = `${backendUrl}/api/mobile-app/download`;
  const isSA = isSuperAdmin(user);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Carregando…</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="page-mobile-app-admin">
      <div>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Smartphone className="w-8 h-8 text-violet-600" />
          App Android "Gestão de Entregas"
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Distribuição do APK pra entregadores via sideload · GPS em background com Foreground Service
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Download className="w-5 h-5 text-violet-600" />
              <div className="font-bold">Status do APK</div>
            </div>
            {info?.available ? (
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-emerald-700 font-bold">
                  <CheckCircle2 className="w-4 h-4" />
                  Publicado — v{info.version}
                </div>
                <div className="text-muted-foreground">
                  <b>Tamanho:</b> {(info.size_bytes / 1024 / 1024).toFixed(1)} MB
                </div>
                <div className="text-muted-foreground">
                  <b>SHA-256:</b> <code className="text-[10px]">{info.sha256?.slice(0, 20)}…</code>
                </div>
                <div className="text-muted-foreground">
                  <b>Atualizado em:</b> {info.updated_at ? new Date(info.updated_at).toLocaleString("pt-BR") : "—"}
                </div>
                <div className="text-muted-foreground" data-testid="apk-env-info">
                  <b>Ambiente:</b> {info.app_env}{info.copied_from_env ? ` (copiado de ${info.copied_from_env})` : ""}
                </div>
                {(info.api_url || info.app_variant) && (
                  <div className="text-muted-foreground" data-testid="apk-variant-info">
                    <b>APK aponta para:</b> <code className="text-[11px]">{info.api_url ? info.api_url.replace(/^https?:\/\//, "") : "—"}</code>
                    {info.app_variant && <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${info.app_variant === "preview" ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`}>{info.app_variant}</span>}
                  </div>
                )}
                {info.last_broadcast && (
                  <div className="text-muted-foreground" data-testid="apk-broadcast-info">
                    <b>Aviso enviado:</b> v{info.last_broadcast.version} → {info.last_broadcast.devices} de {info.last_broadcast.total_tokens} celular(es)
                  </div>
                )}
                <a
                  href={downloadUrl}
                  className="mt-3 inline-flex items-center gap-1 text-violet-700 font-bold hover:underline"
                  target="_blank" rel="noreferrer"
                  data-testid="apk-download-link"
                >
                  <Download className="w-4 h-4" />
                  Baixar APK
                </a>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-amber-800 text-sm">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  Nenhum APK publicado ainda. Faça upload no card ao lado.
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex flex-col items-center">
            <div className="flex items-center gap-2 mb-3 w-full">
              <Smartphone className="w-5 h-5 text-violet-600" />
              <div className="font-bold">QR Code de Download</div>
            </div>
            {info?.available ? (
              <>
                <div className="bg-white p-2 rounded-lg border-2 border-violet-200" data-testid="apk-qr">
                  <QRCodeSVG value={downloadUrl} size={180} level="M" />
                </div>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Entregador aponta a câmera do celular no QR pra baixar o APK diretamente.
                </p>
                <div className="text-[10px] text-slate-500 mt-1 break-all text-center">
                  {downloadUrl}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-8">
                O QR code aparece após o upload do APK.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {isSA && <CopyFromPreviewCard appEnv={info?.app_env} onCopied={load} />}
      {isSA && info?.app_env !== "production" && <ReleaseSlotCard backendUrl={backendUrl} />}

      {isSA && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Upload className="w-5 h-5 text-violet-600" />
              <div className="font-bold">Publicar novo APK</div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Rode <code className="bg-slate-100 px-1 rounded">yarn build:apk</code> (produção) ou <code className="bg-slate-100 px-1 rounded">yarn build:apk:preview</code> (testes) no diretório <code className="bg-slate-100 px-1 rounded">/app/mobile-app/</code>, baixe o .apk gerado pelo EAS e faça upload aqui. O perfil do build define para qual servidor o app aponta — um APK <b>preview</b> nunca é aceito em produção.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="text-xs font-bold uppercase text-slate-600">Versão</label>
                <input
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="w-full border-2 rounded p-2 text-sm mt-1"
                  placeholder="1.0.0"
                  data-testid="apk-version-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase text-slate-600">Arquivo APK</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".apk"
                  className="w-full border-2 rounded p-2 text-sm mt-1"
                  data-testid="apk-file-input"
                />
              </div>
            </div>
            <Button
              onClick={upload}
              disabled={uploading}
              className="mt-4 bg-violet-600 hover:bg-violet-700"
              data-testid="apk-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Enviando…" : "Publicar APK"}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-5">
          <div className="font-bold mb-2">Como buildar o APK</div>
          <ol className="text-sm space-y-2 text-slate-700 list-decimal pl-5">
            <li>Peça o <b>EXPO_TOKEN</b> pra equipe (registre em https://expo.dev)</li>
            <li>No terminal: <code className="bg-slate-100 px-2 py-0.5 rounded">cd /app/mobile-app && export EXPO_TOKEN=... && yarn build:apk</code> (produção → usegestao.app.br) ou <code className="bg-slate-100 px-2 py-0.5 rounded">yarn build:apk:preview</code> (ambiente de testes)</li>
            <li>Aguarde 10-15 min. O EAS retornará URL pra baixar o .apk</li>
            <li>Faça upload aqui no card acima</li>
            <li>Compartilhe o QR code com os entregadores</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
